// @LTG-ID: data/journals/what-is-ebitda
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 3 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "what-is-ebitda",
    FRONTMATTER: {
        number: "40",
        section: "Valuation",
        sectionHref: "/journals#core-valuation",
        sectionAnchor: "core-valuation",
        title: "What Is EBITDA? A Plain-English Guide for Business Owners",
        deck: "EBITDA stands for earnings before interest, taxes, depreciation, and amortization.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-23",
        publishedHuman: "May 23, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 414,
        timeRequired: "PT3M",
        keywords: "what is EBITDA, EBITDA meaning, EBITDA calculation, EBITDA for small business",
        articleSection: "Valuation",
        articleBodyPreview: "EBITDA stands for earnings before interest, taxes, depreciation, and amortization. It measures a company's operating profitability while ignoring how the business is financed, taxed, or how its assets are depreciated. Buyers and lenders use EBITDA to compare businesses on a like for like basis. EBITDA is the most used and most misused word in the language of buying and selling businesses. Here is what it actually is, why anyone cares, and where it misleads. The definition, unpacked Start with ne",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "sde-vs-ebitda-guide",
        "what-is-a-valuation-multiple"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "What Is EBITDA? A Plain-English Guide for Business Owners",
        canonical: "https://letstalkgrowth.com/journals/what-is-ebitda",
        ogImage: "/journals/og/what-is-ebitda.jpg",
    },
    BODY_MARKDOWN: `EBITDA stands for earnings before interest, taxes, depreciation, and amortization. It measures a company's operating profitability while ignoring how the business is financed, taxed, or how its assets are depreciated. Buyers and lenders use EBITDA to compare businesses on a like-for-like basis.

EBITDA is the most used and most misused word in the language of buying and selling businesses. Here is what it actually is, why anyone cares, and where it misleads.

## The definition, unpacked

Start with net income, the bottom line of your profit and loss statement. Then add back four items:

- **Interest**, because how a business is financed is the owner's choice, not a feature of the operations.
- **Taxes**, because tax outcomes depend on entity structure and jurisdiction, which change with new ownership.
- **Depreciation and amortization**, because these are accounting allocations of past purchases, not cash leaving the building this year.

What remains is a rough proxy for the operating cash the business generates, independent of financing and accounting decisions. That neutrality is why a buyer comparing a plumbing company in Charlotte with a machine shop in Ohio reaches for EBITDA: it puts different businesses on the same footing.

## Adjusted EBITDA: where the real conversation happens

In private company deals, the number that matters is usually adjusted EBITDA. Adjustments normalize the earnings for things that will not continue under new ownership: above-market or below-market owner salary, one-time expenses like a lawsuit or a flood repair, personal expenses run through the business, and rent paid to a related party at non-market rates.

Every adjustment will be scrutinized by a buyer's diligence team, so the standard is simple: an adjustment is credible if a reasonable stranger would agree it will not recur. Aggressive add-backs are one of the fastest ways to lose a buyer's trust.

## Where EBITDA misleads

EBITDA ignores capital expenditure. A trucking company must continuously replace trucks; a consulting firm buys laptops. Both can show the same EBITDA while generating very different real cash flow. Buyers know this, which is why capital-intensive businesses often trade at lower multiples of EBITDA than asset-light ones. EBITDA is a comparison tool, not a substitute for understanding cash.

## EBITDA or SDE?

For smaller, owner-operated businesses, the more common earnings measure is seller's discretionary earnings, which treats the owner's full compensation differently. Using the wrong measure with the wrong multiple produces nonsense numbers. We break down the difference in our SDE vs EBITDA guide.`,
    FAQ: [
        { question: "Is EBITDA the same as profit?", answer: "No. EBITDA is profit before interest, taxes, depreciation, and amortization are subtracted. It is typically higher than net income and is used for comparing operating performance, not for measuring what actually lands in the bank." },
        { question: "What is a good EBITDA margin?", answer: "It depends entirely on the industry. Distribution businesses run thin margins at high volume, while professional services can run much higher margins. Compare your margin to businesses in your own sector, not to a universal benchmark." },
        { question: "Do small businesses use EBITDA?", answer: "Businesses large enough to run under professional management are usually measured on EBITDA. Smaller owner-operated businesses are more often measured on seller's discretionary earnings, which accounts for the working owner's compensation." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Valuation",
            title: "SDE vs EBITDA: Which One Applies to Your Business?",
            meta: "3 min · Brief 41",
            to: "/journals/sde-vs-ebitda-guide",
        },
        {
            tag: "Valuation",
            title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
            meta: "3 min · Brief 42",
            to: "/journals/what-is-a-valuation-multiple",
        },
    ],
};

export default BRIEF;
